import { FC, Fragment, useMemo } from 'react';
import { Badge, Box, Link, Typography, Avatar } from '@robocorp/components';
import { styled } from '@robocorp/theme';
import { useTranslation } from 'react-i18next';
import { ReleaseNotesRelease } from './useReleaseNotesQuery';
import { sanitizeHtml } from '~/utils/stringHelper';
import { Timestamp } from '~/components';

const FeaturedImage = styled.img`
  max-width: 100%;
  height: auto;
`;

const TransformTypography = styled(Typography)<{ transform: 'capitalize' | 'uppercase' }>`
  text-transform: ${({ transform }) => transform};
`;

const DescriptionBox = styled(Box)`
  img {
    max-width: 550px;
  }
`;

const getBadgeVariant = (type: ReleaseNotesRelease['type']): 'info' | 'success' | 'warning' => {
  if (type === 'feature') return 'success';
  if (type === 'bugfix') return 'warning';
  return 'info';
};

export const ReleaseNotesEntry: FC<{ release: ReleaseNotesRelease }> = ({ release }) => {
  const { t } = useTranslation();
  const description = useMemo(() => sanitizeHtml(release.description ?? ''), [release.description]);

  return (
    <Box>
      {!!release.featured_image && <FeaturedImage src={release.featured_image} />}
      <Box display="flex" flexDirection="column" px={40} py={18}>
        <Box display="flex" alignItems="center" mb={16}>
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            <Timestamp value={release.released_at * 1000} time={false} year />
            <Box mt={4}>
              <Badge
                label={t(release.type ?? 'update').toUpperCase()}
                variant={getBadgeVariant(release.type)}
                size="small"
              />
            </Box>
          </Box>
          <Box flex={1} />

          <Typography mx={8}>{release.owner.name}</Typography>
          <Avatar src={release.owner.avatar} size="medium" alt={release.owner.name} />
        </Box>

        <Link href={release.url} target="_blank" rel="noreferrer">
          <Typography as="h2" fontFamily="title" fontSize="$24" fontWeight={600} mb={8}>
            {release.title}
          </Typography>
        </Link>

        {!!release.description && (
          <DescriptionBox mb={8}>
            {/* eslint-disable-next-line react/no-danger */}
            <div dangerouslySetInnerHTML={{ __html: description }} />
          </DescriptionBox>
        )}

        {release.notes.length > 0 &&
          release.notes.map((noteSection) => (
            <Fragment key={noteSection.label}>
              <TransformTypography
                fontFamily="title"
                fontSize="$18"
                fontWeight={600}
                as="h3"
                transform="capitalize"
                mb={4}
              >
                {noteSection.display_label}
              </TransformTypography>
              <ul>
                {noteSection.notes.map((note) => (
                  // eslint-disable-next-line react/no-danger
                  <li
                    key={note.title}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(note.title ?? '') }}
                  />
                ))}
              </ul>
            </Fragment>
          ))}
      </Box>
    </Box>
  );
};
